import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios, { AxiosError } from "axios";
import { NewSideBarAdmin } from "../../NewSideBarAdmin";

export interface ModificationDemande {
  id: number | string;
  magasinId: string;
  magasinName: string;
  type: "email" | "password";
  role?: string;
  oldValue?: string;
  newValue: string;
  raw_new_password?: string;
  status: "en_attente" | "approuvé" | "refusé";
  date_creation: string;
}

export default function DemandeModificationAdmin() {
  const navigate = useNavigate();
  const [popup, setPopup] = useState<{
    visible: boolean;
    title: string;
    message: string;
    type: "success" | "error";
  }>({ visible: false, title: "", message: "", type: "success" });

  const [demandes, setDemandes] = useState<ModificationDemande[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState<number | string | null>(null);
  const [activeTab, setActiveTab] = useState<"tous" | "email" | "password">("tous");
  const [searchTerm, setSearchTerm] = useState("");

  // Mobile detection
  const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth <= 768);
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);
    return isMobile;
  };
  const isMobile = useIsMobile();

  // Fetch demandes
  useEffect(() => {
    fetchDemandes();
  }, []);

  const fetchDemandes = () => {
    setLoading(true);
    axios.get(`${process.env.REACT_APP_PHP_APP_URL}/admin/securities`)
      .then((res) => { setDemandes(res.data); setLoading(false); })
      .catch((err) => { console.error(err); setLoading(false); });
  };

  const handleAction = async (id: number | string, action: "approuvé" | "refusé") => {
    setActionLoadingId(id);
    try {
      await axios.post(`${process.env.REACT_APP_PHP_APP_URL}/admin/securities/${id}/action`, { action });

      setDemandes((prev) =>
        prev.map((d) => (d.id === id ? { ...d, status: action } : d))
      );

      setPopup({
        visible: true,
        title: action === "approuvé" ? "Approuvé !" : "Refusé",
        message: `La demande a été ${action} avec succès.`,
        type: action === "approuvé" ? "success" : "error",
      });

      setTimeout(() => setPopup((prev) => ({ ...prev, visible: false })), 3000);
    } catch (error) {
      setPopup({
        visible: true,
        title: "Erreur !",
        message: "Une erreur est survenue.",
        type: "error",
      });
      setTimeout(() => setPopup((prev) => ({ ...prev, visible: false })), 3000);
    } finally {
      setActionLoadingId(null);
    }
  };

  const filteredDemandes = useMemo(() => {
    return demandes.filter((d) => {
      const matchesSearch =
        d.magasinName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.newValue.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesTab = activeTab === "tous" || d.type === activeTab;
      
      return matchesSearch && matchesTab;
    });
  }, [demandes, searchTerm, activeTab]);

  const getStatusConfig = (status: string) => {
    switch (status) {
      case "approuvé":
        return { color: "#059669", bg: "#d1fae5", icon: "check-circle-fill", text: "Approuvé" };
      case "refusé":
        return { color: "#dc2626", bg: "#fee2e2", icon: "x-circle-fill", text: "Refusé" };
      default:
        return { color: "#d97706", bg: "#fef3c7", icon: "clock-fill", text: "En attente" };
    }
  };

  // Styles definition
  const styles = {
    pageContainer: {
      maxWidth: "1400px",
      margin: "0 auto",
      padding: isMobile ? "20px 16px" : "40px 24px",
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
      animation: "slideUp 0.5s ease-out",
    },
    pageHeader: {
      marginBottom: "32px",
    },
    pageTitle: {
      fontSize: isMobile ? "1.75rem" : "2.25rem",
      fontWeight: 700,
      color: "#1a1a1a",
      margin: "0 0 8px 0",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      letterSpacing: "-0.02em",
    },
    titleIcon: {
      color: "#ffc400",
      fontSize: isMobile ? "1.5rem" : "2rem",
    },
    pageSubtitle: {
      fontSize: "1rem",
      color: "#6b7280",
      margin: 0,
      fontWeight: 400,
    },
    controlsCard: {
      background: "#ffffff",
      borderRadius: "16px",
      padding: isMobile ? "16px" : "24px",
      marginBottom: "24px",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
      border: "1px solid #e5e7eb",
    },
    controlsFlex: {
      display: "flex",
      flexDirection: isMobile ? "column" as const : "row" as const,
      gap: "16px",
      justifyContent: "space-between",
      alignItems: isMobile ? "stretch" : "center",
    },
    tabsContainer: {
      display: "flex",
      gap: "8px",
      background: "#f3f4f6",
      padding: "6px",
      borderRadius: "12px",
    },
    tabButton: (isActive: boolean) => ({
      padding: "10px 16px",
      borderRadius: "8px",
      border: "none",
      fontSize: "0.95rem",
      fontWeight: 600,
      cursor: "pointer",
      fontFamily: "'Outfit', sans-serif",
      transition: "all 0.3s",
      background: isActive ? "#ffffff" : "transparent",
      color: isActive ? "#1a1a1a" : "#6b7280",
      boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
      flex: isMobile ? 1 : "auto",
    }),
    searchContainer: {
      position: "relative" as const,
      minWidth: isMobile ? "100%" : "300px",
    },
    searchIcon: {
      position: "absolute" as const,
      left: "16px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#6b7280",
    },
    searchInput: {
      width: "100%",
      padding: "12px 16px 12px 44px",
      border: "2px solid #e5e7eb",
      borderRadius: "12px",
      fontSize: "0.95rem",
      color: "#1a1a1a",
      fontFamily: "'Outfit', sans-serif",
      boxSizing: "border-box" as const,
    },
    tableCard: {
      background: "#ffffff",
      borderRadius: "16px",
      overflow: "hidden",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
      border: "1px solid #e5e7eb",
    },
    table: {
      width: "100%",
      borderCollapse: "separate" as const,
      borderSpacing: 0,
    },
    th: {
      padding: "16px 20px",
      background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
      color: "#ffffff",
      textAlign: "left" as const,
      fontSize: "0.85rem",
      fontWeight: 600,
      textTransform: "uppercase" as const,
      letterSpacing: "0.05em",
      whiteSpace: "nowrap" as const,
    },
    td: {
      padding: "16px 20px",
      fontSize: "0.95rem",
      color: "#1a1a1a",
      fontWeight: 500,
      borderBottom: "1px solid #e5e7eb",
    },
    actionButtons: {
      display: "flex",
      gap: "8px",
    },
    btnApprove: {
      padding: "8px 16px",
      background: "#059669",
      color: "white",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      fontWeight: 600,
      fontFamily: "'Outfit', sans-serif",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
    btnReject: {
      padding: "8px 16px",
      background: "#dc2626",
      color: "white",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      fontWeight: 600,
      fontFamily: "'Outfit', sans-serif",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
    statusBadge: (status: string) => {
      const config = getStatusConfig(status);
      return {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px 12px",
        borderRadius: "100px",
        fontSize: "0.85rem",
        fontWeight: 600,
        color: config.color,
        backgroundColor: config.bg,
      };
    },
    typeBadge: (type: string) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "6px 12px",
      borderRadius: "8px",
      fontSize: "0.85rem",
      fontWeight: 600,
      color: type === "email" ? "#2563eb" : "#7c3aed",
      backgroundColor: type === "email" ? "#dbeafe" : "#ede9fe",
    }),
    popup: (type: "success" | "error") => ({
      position: "fixed" as const,
      top: "20px",
      right: "20px",
      zIndex: 9999,
      padding: "20px 24px",
      borderRadius: "16px",
      border: "1px solid #ffffff",
      color: "#ffffff",
      backgroundColor: type === "success" ? "#059669" : "#dc2626",
      boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
      minWidth: "300px",
      animation: "slideInRight 0.3s ease-out",
    }),
  };

  const styleSheet = `
    @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');
    
    @keyframes slideUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes slideInRight {
      from { opacity: 0; transform: translateX(100px); }
      to { opacity: 1; transform: translateX(0); }
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    input:focus {
      outline: none !important;
      border-color: #ffc400 !important;
      box-shadow: 0 0 0 3px rgba(255, 196, 0, 0.1) !important;
    }
    button:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
    button:active { transform: translateY(0); }
    table tbody tr:hover { background: rgba(255, 196, 0, 0.03); }
    
    /* Custom scrollbar for table container */
    .table-container {
      overflow-x: auto;
    }
    .table-container::-webkit-scrollbar {
      height: 8px;
    }
    .table-container::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }
    .table-container::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 4px;
    }
  `;

  return (
    <>
      <style>{styleSheet}</style>
      <NewSideBarAdmin />
      
      {popup.visible && (
        <div style={styles.popup(popup.type)}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", fontWeight: 700, fontSize: "1.1rem", marginBottom: "8px" }}>
            <i className={`bi bi-${popup.type === "success" ? "check-circle" : "x-circle"}`}></i>
            {popup.title}
          </div>
          <p style={{ margin: 0, paddingLeft: "26px", fontSize: "0.95rem", opacity: 0.9 }}>{popup.message}</p>
        </div>
      )}

      <div style={styles.pageContainer}>
        {/* Header */}
        <div style={styles.pageHeader}>
          <h1 style={styles.pageTitle}>
            <i className="bi bi-shield-lock" style={styles.titleIcon}></i>
            Demandes de modification
          </h1>
          <p style={styles.pageSubtitle}>
            Gérez les demandes de changement d'email et de mot de passe des magasins
          </p>
        </div>

        {/* Controls */}
        <div style={styles.controlsCard}>
          <div style={styles.controlsFlex}>
            <div style={styles.tabsContainer}>
              <button style={styles.tabButton(activeTab === "tous")} onClick={() => setActiveTab("tous")}>
                Toutes
              </button>
              <button style={styles.tabButton(activeTab === "email")} onClick={() => setActiveTab("email")}>
                <i className="bi bi-envelope" style={{marginRight: "6px"}}></i>Emails
              </button>
              <button style={styles.tabButton(activeTab === "password")} onClick={() => setActiveTab("password")}>
                <i className="bi bi-key" style={{marginRight: "6px"}}></i>Mots de passe
              </button>
            </div>
            
            <div style={styles.searchContainer}>
              <i className="bi bi-search" style={styles.searchIcon}></i>
              <input

                type="text"
                placeholder="Rechercher un magasin..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px" }}>
            <div className="spinner-border text-warning" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
          </div>
        ) : filteredDemandes.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 20px", background: "#ffffff", borderRadius: "16px", border: "2px dashed #e5e7eb" }}>
            <i className="bi bi-inbox" style={{ fontSize: "3rem", color: "#9ca3af", marginBottom: "16px", display: "block" }}></i>
            <h3 style={{ fontSize: "1.2rem", color: "#4b5563", margin: 0 }}>Aucune demande trouvée</h3>
          </div>
        ) : (
          <div style={styles.tableCard}>
            <div className="table-container">
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Utilisateur</th>
                    <th style={styles.th}>Rôle</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Ancienne Valeur</th>
                    <th style={styles.th}>Nouvelle Valeur</th>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Statut</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDemandes.map((demande) => (
                    <tr key={demande.id}>
                      <td style={styles.td}>
                        <div style={{ fontWeight: 600 }}>{demande.magasinName}</div>
                        <div style={{ fontSize: "0.85rem", color: "#6b7280" }}>Demande N° : {demande.id}</div>
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: "4px",
                          padding: "4px 10px", borderRadius: "100px", fontSize: "0.8rem", fontWeight: 600,
                          background: demande.role === "visiteur" ? "#fef3c7" : "#e0e7ff",
                          color: demande.role === "visiteur" ? "#d97706" : "#4338ca",
                        }}>
                          <i className={`bi bi-${demande.role === "visiteur" ? "person" : "shop"}`} />
                          {demande.role ?? "magasin"}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.typeBadge(demande.type)}>
                          <i className={`bi bi-${demande.type === "email" ? "envelope" : "key"}`} />
                          {demande.type === "email" ? "Email" : "Mot de passe"}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{ color: "#6b7280" }}>{demande.oldValue || "—"}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={{ fontWeight: 600, fontFamily: "monospace" }}>
                          {demande.type === "password" ? (demande.raw_new_password ?? demande.newValue) : demande.newValue}
                        </span>
                      </td>
                      <td style={styles.td}>{demande.date_creation}</td>
                      <td style={styles.td}>
                        <span style={styles.statusBadge(demande.status)}>
                          <i className={`bi bi-${getStatusConfig(demande.status).icon}`}></i>
                          {getStatusConfig(demande.status).text}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {demande.status === "en_attente" && (
                          actionLoadingId === demande.id ? (
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#6b7280", fontSize: "0.9rem" }}>
                              <div
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  border: "2px solid #e5e7eb",
                                  borderTopColor: "#ffc400",
                                  borderRadius: "50%",
                                  animation: "spin 0.7s linear infinite",
                                  flexShrink: 0,
                                }}
                              />
                              En cours...
                            </div>
                          ) : (
                            <div style={styles.actionButtons}>
                              <button
                                style={styles.btnApprove}
                                onClick={() => handleAction(demande.id, "approuvé")}
                                disabled={actionLoadingId !== null}
                                title="Approuver"
                              >
                                <i className="bi bi-check-lg"></i>
                              </button>
                              <button
                                style={{ ...styles.btnReject, opacity: actionLoadingId !== null ? 0.5 : 1 }}
                                onClick={() => handleAction(demande.id, "refusé")}
                                disabled={actionLoadingId !== null}
                                title="Refuser"
                              >
                                <i className="bi bi-x-lg"></i>
                              </button>
                            </div>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
